<script lang="ts">
	import type { Store, State } from './store'
	import type { SvelteAsr } from 'types/common'
	import type { Mediator } from 'types/mediator'
	import type { i18n } from 'types/common'

	import Input from '@isoftdata/svelte-input'
	import Textarea from '@isoftdata/svelte-textarea'
	import Select from '@isoftdata/svelte-select'
	import SiteDetails from '@isoftdata/svelte-site-details'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import SaveResetButton from '@isoftdata/svelte-save-reset-button'
	import { graphql, AutofillByZipStore } from '$houdini'
	import { getContext } from 'svelte'
	import { makeCrudStore } from '@isoftdata/svelte-store-crud'
	import session from 'stores/session'

	interface Props {
		asr: SvelteAsr
		canConfigureStores?: boolean
		selectedStoreUuid: string
		states?: Array<State>
		stores?: Array<Store>
		userStores?: Array<number>
	}

	let {
		asr,
		canConfigureStores = false,
		selectedStoreUuid = $bindable(),
		states = [],
		stores = $bindable([]),
		userStores = [],
	}: Props = $props()

	const { t: translate } = getContext<i18n>('i18next')
	const mediator = getContext<Mediator>('mediator')

	const siteCrudStore = makeCrudStore<Store, 'uuid'>('uuid')

	const taxServices = [
		//Const for now until fetched from server
		{ value: 'NONE', label: translate('common:none', 'None') },
		{ value: 'MOCK', label: translate('common:mock', 'Mock') },
		{ value: 'TAXJAR', label: translate('common:taxjar', 'TaxJar') },
		{ value: 'DS_TAX', label: translate('common:dstax', 'DSTax') },
	]
	const addressServices = [
		//Const for now until fetched from server
		{ value: 'NONE', label: translate('common:none', 'None') },
		{ value: 'MOCK', label: translate('common:mock', 'Mock') },
		{ value: 'TAXJAR', label: translate('common:taxjar', 'TaxJar') },
	]

	if (import.meta.env.DEV) {
		taxServices.push({ value: 'TAXJAR_SANDBOX', label: 'TaxJar Sandbox' })
	}

	// Defined in AddressFields.svelte; operation names must be unique, so reuse its generated store
	const autofillByZipQuery = new AutofillByZipStore()

	const updateStoreMutation = graphql(`
		mutation UpdateStore($input: StoreUpdate!) {
			updateStore(input: $input) {
				id
			}
		}
	`)

	function getStorePermissions(storeId: number | null) {
		if (!storeId) {
			return false
		}
		return userStores.includes(storeId)
	}

	function markStoreDirty() {
		siteCrudStore.update(stores[selectedStoreIndex])
	}

	function updateRemittanceAddress() {
		if (stores[selectedStoreIndex].remittanceIsMainAddress) {
			stores[selectedStoreIndex].remittanceAddress = stores[selectedStoreIndex].street
			stores[selectedStoreIndex].remittanceCity = stores[selectedStoreIndex].city
			stores[selectedStoreIndex].remittanceState = stores[selectedStoreIndex].state
			stores[selectedStoreIndex].remittanceZip = stores[selectedStoreIndex].zip
			stores[selectedStoreIndex].remittanceCountry = stores[selectedStoreIndex].country
			stores[selectedStoreIndex].remittancePhone = stores[selectedStoreIndex].phone
			markStoreDirty()
		}
	}

	function toggleRemittanceAddress() {
		stores[selectedStoreIndex].remittanceIsMainAddress = !stores[selectedStoreIndex].remittanceIsMainAddress
		updateRemittanceAddress()
	}

	async function getLocationDataFromZip() {
		const zip = stores[selectedStoreIndex].zip
		if (!zip) {
			return
		}
		const { data } = await autofillByZipQuery.fetch({ variables: { zipcode: zip } })
		const autofillByZip = data?.autofillByZip
		if (!autofillByZip) {
			return
		}
		stores[selectedStoreIndex].city = autofillByZip.city ?? ''
		stores[selectedStoreIndex].state =
			states.find(state => state.abbreviation === autofillByZip.stateAbbreviation)?.abbreviation ?? ''
		stores[selectedStoreIndex].country = autofillByZip.country ?? ''

		markStoreDirty()
	}

	async function saveStore() {
		mediator.publish('showMessage', {
			heading: translate('configuration.store.savingDotDotDot', 'Saving...'),
			message: '',
			type: 'info',
			time: false,
		})

		const storesToUpdate = siteCrudStore.updatedValues

		try {
			storesToUpdate.length > 0
				? await Promise.all(
						storesToUpdate.map(store =>
							updateStoreMutation.mutate({
								input: {
									id: store.id,
									addressService: {
										service: store.addressService.service,
										apiKey: store.addressService.apiKey,
									},
									taxService: {
										service: store.taxService.service,
										apiKey: store.taxService.apiKey,
									},
									code: store.code,
									name: store.name,
									address: store.street,
									city: store.city,
									state: store.state,
									zip: store.zip,
									country: store.country,
									phone: store.phone,
									fax: store.fax,
									remittanceAddress: store.remittanceAddress,
									remittanceCity: store.remittanceCity,
									remittanceState: store.remittanceState,
									remittanceZip: store.remittanceZip,
									remittanceCountry: store.remittanceCountry,
									remittancePhone: store.remittancePhone,
									webAddress: store.webAddress,
									invoiceMemo: store.invoiceMemo,
									private: store.private,
								},
							}),
						),
					)
				: null
			// Set this to false to not trip `canLeaveState`
			storesChanged = false
			mediator.publish('showMessage', {
				heading: translate('common:savedHeading', 'Saved!'),
				message: translate('configuration.store.storesSavedSuccessfully.', 'Stores saved successfully.'),
				type: 'success',
				time: 10000,
			})
			// Reload the page after a successful save, but not after an error
			asr.go(null, { lastResetTime: null, lastSavedTime: Date.now() }, { inherit: true })
		} catch (err) {
			const theError = err instanceof Error ? err : new Error(translate('dashboard.unknownError', 'Unknown Error'))
			console.error(theError)
			mediator.publish('showMessage', {
				heading: translate('configuration.store.errorSavingStores', 'Error Saving Stores'),
				message: theError.message,
				type: 'danger',
				time: false,
			})
		}
	}

	export function canLeaveState() {
		return (
			!storesChanged ||
			confirm(
				translate(
					'common:youHaveUnsavedChangesAreYouSureYouWantToLeave',
					'You have unsaved changes. Are you sure you want to leave?',
				),
			)
		)
	}

	const license = $derived($session.licenseInfo.limits.STORE_LIMIT)
	const belowLimit = $derived(
		license?.belowLimit &&
			(license.limit === null || (siteCrudStore.createdValues?.length ?? 0) + license.count < license.limit),
	)
	let storesChanged = $derived($siteCrudStore && siteCrudStore.updatedValues.length > 0)
	const selectedStoreIndex = $derived(stores.findIndex(store => store.uuid === selectedStoreUuid))
	const canEditSelectedStore = $derived(getStorePermissions(stores[selectedStoreIndex]?.id))
</script>

<div class="row">
	<div class="col-lg-3 col-md-3 col-sm-6 pe-1">
		<Select
			showEmptyOption={false}
			label={translate('configuration.store.selectStore', 'Select Store')}
			options={stores}
			bind:value={selectedStoreUuid}
		>
			{#snippet option({ option: store })}
				<option value={store?.uuid}>{store?.code} - {store?.name}</option>
			{/snippet}
		</Select>
	</div>
	<div class="col-12 col-sm text-end">
		<SaveResetButton
			disabled={!storesChanged || !canEditSelectedStore}
			resetHref={asr.makePath(null, { lastResetTime: Date.now(), lastSavedTime: null }, { inherit: true })}
			save={() => saveStore()}
		/>
	</div>
</div>

<div class="row">
	<div class="col-12 col-md-4 mb-2">
		<SiteDetails
			showTimezone={false}
			hideCreateDeleteButtons={true}
			{states}
			{siteCrudStore}
			siteAlias="Store"
			canCreateSite={belowLimit && false}
			canEditSelectedSite={canEditSelectedStore && canConfigureStores}
			bind:sites={stores}
			bind:selectedSiteUuid={selectedStoreUuid}
			updateLocationFieldsFromZip={getLocationDataFromZip}
			fieldChange={() => updateRemittanceAddress()}
		/>
	</div>
	<div class="col-12 col-md-4 mb-2">
		<div class="card h-100">
			<div class="card-header">
				<h5 class="mb-0">{translate('configuration.store.remittanceAddress', 'Remittance Address')}</h5>
			</div>
			<div class="card-body">
				<div class="row">
					<div class="col-12">
						<Checkbox
							disabled={!canEditSelectedStore || !canConfigureStores}
							label={translate(
								'configuration.store.remittanceAddressIsTheSameAsPhysicalAddress',
								'Remittance address is the same as physical address',
							)}
							checked={stores[selectedStoreIndex].remittanceIsMainAddress}
							onchange={toggleRemittanceAddress}
						/>
					</div>
					<div class="col-12">
						<Input
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							label={translate('common:address', 'Address')}
							placeholder={translate('common:streetAddress', 'Street Address')}
							bind:value={stores[selectedStoreIndex].remittanceAddress}
							onchange={markStoreDirty}
						/>
					</div>
					<div class="col-md-4 col-6">
						<Input
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							label={translate('common:city', 'City')}
							placeholder={translate('common:city', 'City')}
							bind:value={stores[selectedStoreIndex].remittanceCity}
							onchange={markStoreDirty}
						/>
					</div>
					<div class="col-md-3 col-6">
						<Select
							showEmptyOption={true}
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							label={translate('common:state', 'State')}
							emptyValue={''}
							options={states}
							bind:value={stores[selectedStoreIndex].remittanceState}
							onchange={markStoreDirty}
						>
							{#snippet option({ option: state })}
								<option value={state?.abbreviation}>{state?.name}</option>
							{/snippet}
						</Select>
					</div>
					<div class="col-md-2 col-6">
						<Input
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							placeholder={translate('common:zip', 'Zip')}
							label={translate('common:zip', 'Zip')}
							bind:value={stores[selectedStoreIndex].remittanceZip}
							onchange={markStoreDirty}
						/>
					</div>
					<div class="col-md-3 col-6">
						<Input
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							placeholder={translate('common:country', 'Country')}
							label={translate('common:country', 'Country')}
							bind:value={stores[selectedStoreIndex].remittanceCountry}
							onchange={markStoreDirty}
						/>
					</div>
					<div class="col-12">
						<Input
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							label={translate('configuration.store.remittancePhone', 'Remittance Phone')}
							placeholder={translate('common:phoneNum', 'Phone #')}
							bind:value={stores[selectedStoreIndex].remittancePhone}
							onchange={markStoreDirty}
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div class="col-12 col-md-4 mb-2">
		<div class="card h-100">
			<div class="card-header">
				<h5 class="mb-0">{translate('configuration.store.miscDetails', 'Misc Details')}</h5>
			</div>
			<div class="card-body">
				<Input
					disabled={!canEditSelectedStore || !canConfigureStores}
					placeholder={translate('common:url', 'URL')}
					label={translate('common:webAddress', 'Web Address')}
					bind:value={stores[selectedStoreIndex].webAddress}
					onchange={markStoreDirty}
				/>
				<Textarea
					disabled={!canEditSelectedStore || !canConfigureStores}
					label={translate('configuration.store.invoiceMemo', 'Invoice Memo')}
					bind:value={stores[selectedStoreIndex].invoiceMemo}
					onchange={markStoreDirty}
				/>
			</div>
		</div>
	</div>
</div>
<div class="row">
	<div class="col-12 col-md-6 mb-2">
		<div class="card">
			<div class="card-header">
				<h5 class="mb-0">{translate('configuration.store.taxService', 'Tax Service')}</h5>
			</div>
			<div class="card-body">
				<Select
					showEmptyOption={false}
					label={translate('configuration.store.taxService', 'Tax Service')}
					disabled={!canConfigureStores || !canEditSelectedStore}
					options={taxServices}
					bind:value={stores[selectedStoreIndex].taxService.service}
					onchange={markStoreDirty}
				>
					{#snippet option({ option: taxService })}
						<option value={taxService?.value}>{taxService?.label}</option>
					{/snippet}
				</Select>
				<Input
					type="password"
					label={translate('configuration.store.taxServiceApiKey', 'Tax Service API Key')}
					disabled={!canConfigureStores || !canEditSelectedStore}
					bind:value={stores[selectedStoreIndex].taxService.apiKey}
					onchange={markStoreDirty}
				/>
			</div>
		</div>
	</div>
	<div class="col-12 col-md-6">
		<div class="card">
			<div class="card-header">
				<h5 class="mb-0">{translate('configuration.store.addressService', 'Address Service')}</h5>
			</div>
			<div class="card-body">
				<Select
					showEmptyOption={false}
					label={translate('configuration.store.addressService', 'Address Service')}
					disabled={!canConfigureStores || !canEditSelectedStore}
					options={addressServices}
					bind:value={stores[selectedStoreIndex].addressService.service}
					onchange={markStoreDirty}
				>
					{#snippet option({ option: addressService })}
						<option value={addressService?.value}>{addressService?.label}</option>
					{/snippet}
				</Select>
				<Input
					type="password"
					label={translate('configuration.store.addressServiceApiKey', 'Address Service API Key')}
					disabled={!canConfigureStores || !canEditSelectedStore}
					bind:value={stores[selectedStoreIndex].addressService.apiKey}
					onchange={markStoreDirty}
				/>
			</div>
		</div>
	</div>
</div>
